import { AnimatePresence } from "framer-motion";
import { PlugZapIcon, PlusIcon } from "lucide-react";
import { useCallback } from "react";

import { Button } from "../../Button";

import { OpenaiYamlDependenciesEmptyCard } from "./DependenciesEmptyCard";
import { createEmptyTool } from "./field-config";
import { OPENAI_YAML_TOOL_LIST_MAX_HEIGHT_CLASS } from "./form-constants";
import { OpenaiYamlToolCard } from "./ToolCard";
import type { ToolDependency } from "./types";
import { useToolListAutoScroll } from "./useToolListAutoScroll";

export interface OpenaiYamlToolDependenciesEditorProps {
  tools: ToolDependency[];
  onChange: (tools: ToolDependency[]) => void;
  readOnly?: boolean;
}

export const OpenaiYamlToolDependenciesEditor = ({
  tools,
  onChange,
  readOnly = false,
}: OpenaiYamlToolDependenciesEditorProps) => {
  const { scrollContainerRef, lastItemRef } =
    useToolListAutoScroll(tools);

  const addTool = useCallback(() => {
    onChange([...tools, createEmptyTool()]);
  }, [tools, onChange]);

  const removeTool = useCallback(
    (index: number) => {
      onChange(tools.filter((_, i) => i !== index));
    },
    [tools, onChange],
  );

  const updateTool = useCallback(
    (index: number, patch: Partial<ToolDependency>) => {
      onChange(tools.map((t, i) => (i === index ? { ...t, ...patch } : t)));
    },
    [tools, onChange],
  );

  if (readOnly && tools.length === 0) {
    return (
      <OpenaiYamlDependenciesEmptyCard
        icon={PlugZapIcon}
        title="No tool dependencies"
        description="This OpenAI agent configuration does not include any dependencies."
      />
    );
  }

  return (
    <div className="ml-3 space-y-3">
      <div
        ref={scrollContainerRef}
        className={`${OPENAI_YAML_TOOL_LIST_MAX_HEIGHT_CLASS} overflow-y-auto pr-1`}
      >
        <div className="flex flex-col gap-3">
          <AnimatePresence mode="popLayout">
            {tools.map((tool, index) => (
              <div
                key={tool._id}
                ref={index === tools.length - 1 ? lastItemRef : undefined}
              >
                <OpenaiYamlToolCard
                  tool={tool}
                  index={index}
                  readOnly={readOnly}
                  onRemove={removeTool}
                  onUpdate={updateTool}
                />
              </div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      {!readOnly ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={addTool}
        >
          <PlusIcon className="mr-1 h-3.5 w-3.5" />
          Add tool dependency
        </Button>
      ) : null}
    </div>
  );
};
